import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { useAccount } from '../account/store.ts';
import { api } from '../api/instance.ts';
import { errorMessage, toast } from '../state/feedback.ts';
import { IconButton } from '../ui/IconButton.tsx';

type Rating = 'up' | 'down';
const KEY = 'better-lights.render-feedback.v1';

function load(): Record<string, Rating> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, Rating>;
  } catch {
    return {};
  }
}

/** Sample results of the demo account have no render on the server to rate. */
export const canRate = (aiResultId: string) => !aiResultId.startsWith('ai-demo-');

/** Thumbs up / down on a render image: saved to the server (per user), remembered on this device. */
export function RenderFeedback({ aiResultId }: { aiResultId: string }) {
  const signedIn = useAccount((s) => !!s.session);
  const [rating, setRating] = useState<Rating | null>(() => load()[aiResultId] ?? null);
  if (!signedIn || !canRate(aiResultId)) return null;

  const rate = async (next: Rating) => {
    const value = rating === next ? null : next; // tapping the current choice clears it
    const before = rating;
    setRating(value);
    try {
      await api.setRenderFeedback(aiResultId, value ?? 'none');
      const all = load();
      if (value) all[aiResultId] = value;
      else delete all[aiResultId];
      localStorage.setItem(KEY, JSON.stringify(all));
      if (value) toast(value === 'up' ? 'Thanks! Glad this one worked.' : 'Thanks, this helps us improve renders.');
    } catch (e) {
      setRating(before);
      toast(errorMessage(e, 'Your feedback could not be saved.'), 'error');
    }
  };

  return (
    <div className="flex items-center gap-1" role="group" aria-label="Rate this render">
      <IconButton icon={ThumbsUp} label="Good render" active={rating === 'up'} onClick={() => void rate('up')} />
      <IconButton icon={ThumbsDown} label="Bad render" active={rating === 'down'} onClick={() => void rate('down')} />
    </div>
  );
}
